<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';

export default {
  components: {
    LoadingState,
    NextButton,
    SelectInput,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    currentInboxId() {
      return this.inbox?.id || this.$route.params.inboxId;
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](
        this.currentInboxId
      );
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
  },
  mounted() {
    this.fetchBotData();
  },

  methods: {
    fetchBotData() {
      this.$store.dispatch('agentBots/get');
      this.$store.dispatch('agentBots/fetchAgentBotInbox', this.currentInboxId);
    },
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-5 max-w-4xl sm:mx-6">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form
      v-else
      class="rounded-2xl border border-n-weak bg-n-alpha-1 p-5 shadow-sm sm:p-6"
      @submit.prevent="updateActiveAgentBot"
    >
      <div class="mb-5 flex items-start gap-3">
        <span
          class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <span class="i-lucide-bot size-5" />
        </span>
        <div>
          <h2 class="mb-0 text-heading-3 text-n-slate-12">
            {{ $t('AGENT_BOTS.BOT_CONFIGURATION.TITLE') }}
          </h2>
          <p class="mb-0 mt-1 max-w-2xl text-sm text-n-slate-10">
            {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DESC') }}
          </p>
        </div>
      </div>
      <div class="grid items-end gap-3 sm:grid-cols-[minmax(0,28rem)_auto]">
        <SelectInput
          v-model="selectedAgentBotId"
          :placeholder="$t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER')"
          :options="agentBots.map(bot => ({ value: bot.id, label: bot.name }))"
        />
        <div class="flex flex-col gap-2 sm:flex-row">
          <NextButton
            type="submit"
            :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
            :is-loading="uiFlags.isSettingAgentBot"
          />
          <NextButton
            type="button"
            :disabled="!selectedAgentBotId"
            :is-loading="uiFlags.isDisconnecting"
            faded
            ruby
            @click="disconnectBot"
          >
            {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
          </NextButton>
        </div>
      </div>
    </form>
  </div>
</template>
